import React from 'react';
import type { RecentPaymentDto, PaymentMethodBreakdown } from '../../../../types/api-responses';
import styles from './RecentPayments.module.css';

interface RecentPaymentsProps {
    payments: RecentPaymentDto[];
}

const getStatusClass = (status: string): string => {
    const s = status.toLowerCase();
    if (s === 'paid' || s === 'captured' || s === 'completed') return styles.statusPaid;
    if (s === 'pending') return styles.statusPending;
    if (s === 'failed') return styles.statusFailed;
    if (s === 'refunded') return styles.statusRefunded;
    return '';
};

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const RecentPayments: React.FC<RecentPaymentsProps> = ({ payments }) => {
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.title}>Recent Payments</h3>
            </div>
            {payments.length === 0 ? (
                <div className={styles.emptyState}>No recent payments</div>
            ) : (
                <div className={styles.paymentList}>
                    {payments.slice(0, 5).map((payment) => (
                        <div key={payment.id} className={styles.paymentItem}>
                            <div className={styles.paymentLeft}>
                                <span className={styles.paymentCustomer}>
                                    {payment.customerName || `Appointment #${payment.appointmentId}`}
                                </span>
                                <span className={styles.paymentDate}>
                                    {formatDate(payment.date)} via {payment.method}
                                </span>
                            </div>
                            <div className={styles.paymentRight}>
                                <span className={styles.paymentAmount}>
                                    {payment.amount.toFixed(2)} {(payment.currency || 'AED').toUpperCase()}
                                </span>
                                <span className={`${styles.statusBadge} ${getStatusClass(payment.status)}`}>
                                    {payment.status}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RecentPayments;

interface PaymentMethodBreakdownCardProps {
    breakdown: PaymentMethodBreakdown[];
}

export const PaymentMethodBreakdownCard: React.FC<PaymentMethodBreakdownCardProps> = ({ breakdown }) => {
    return (
        <div className={styles.breakdownContainer}>
            <div className={styles.header}>
                <h3 className={styles.title}>Revenue by Payment Method</h3>
            </div>
            {breakdown.length === 0 ? (
                <div className={styles.emptyState}>No payment data available</div>
            ) : (
                <div className={styles.breakdownList}>
                    {breakdown.map((item, index) => (
                        <div key={index} className={styles.breakdownItem}>
                            <span className={styles.breakdownMethod}>{item.method}</span>
                            <div className={styles.breakdownStats}>
                                <span className={styles.breakdownCount}>{item.count} txns</span>
                                <span className={styles.breakdownAmount}>{item.amount.toFixed(2)} AED</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
